import React, { ChangeEvent, useEffect, useState } from "react";
import {
    Button,
    Card,
    Col,
    FloatingLabel,
    Form,
    Image,
    Row,
    Toast,
    ToastContainer,
} from "react-bootstrap";
import { useAuth } from "../../../../../providers";
import { AdmPanelContainer } from "../../../../adm-panel/site/api/screens";
import { AdsStrings, IProductsItemToAdsCreate } from "../../../business";
import { AdsCreationControllerImpl } from "../../../business/impl/controllers";

export function AdsCreatePage({ navigate }) {
    const { setCallbackLogin, handleLogout, userData } = useAuth();
    const strings = AdsStrings['PTBR'].creation.form;
    const controller = new AdsCreationControllerImpl();
    const [selectedImages, setSelectedImages] = useState<File[]>([]);
    const [imagePreviews, setImagePreviews] = useState<string[]>([]);
    const [toasting, setToasting] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isProccessing, setProccessing] = useState(false);
    const [myProducts, setMyProducts] = useState<IProductsItemToAdsCreate[]>([])

    const calculateTimeDifference = (targetDateTime) => {
        const now = new Date();
        const targetDate = new Date(targetDateTime);
        const diffInSeconds = Math.floor((now - targetDate) / 1000);

        if (diffInSeconds < 30) {
            return "Agora";
        } else if (diffInSeconds < 60) {
            return "Neste minuto";
        } else if (diffInSeconds < 3600) {
            const diffInMinutes = Math.floor(diffInSeconds / 60);
            return `Há ${diffInMinutes} minuto${diffInMinutes === 1 ? "" : "s"}`;
        } else if (diffInSeconds < 86400) {
            const diffInHours = Math.floor(diffInSeconds / 3600);
            return `Há ${diffInHours} hora${diffInHours === 1 ? "" : "s"}`;
        } else if (diffInSeconds < 604800) {
            const diffInDays = Math.floor(diffInSeconds / 86400);
            return `Há ${diffInDays} dia${diffInDays === 1 ? "" : "s"}`;
        } else if (diffInSeconds < 2629743) {
            const diffInWeeks = Math.floor(diffInSeconds / 604800);
            return `Há ${diffInWeeks} semana${diffInWeeks === 1 ? "" : "s"}`;
        } else {
            const diffInMonths = Math.floor(diffInSeconds / 2629743);
            return `Há ${diffInMonths} mês${diffInMonths === 1 ? "" : "es"}`;
        }
    };

    const [formData, setFormData] = useState({
        productId: "",
        description: "",
        showDescription: true
    });
    const {
        productId,
        description,
        showDescription
    } = formData;


    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(e.target.files || []);
        setSelectedImages(selectedFiles);

        const previews = selectedFiles.map((file) => URL.createObjectURL(file));
        setImagePreviews(previews);
    };

    useEffect(() => {
        return () => {
            imagePreviews.forEach((preview) => URL.revokeObjectURL(preview));
        };
    }, [imagePreviews]);

    const fetchData = async () => {
        try {
            const response = await controller.onLoad();
            setMyProducts(response);
        } catch (error) {
            console.error("Erro na requisição:", error);
            if (error === "401") {
                setCallbackLogin(
                    {
                        goBack: true,
                        origin: '/adm/dashboard',
                    }
                );
                handleLogout();
                navigate('/auth');
            } else {
                setIsLoading(true);
            }
        }
    }

    useEffect(() => {
        fetchData();
    }, [userData]);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setProccessing(true);
        const submitedFormData = new FormData();
        submitedFormData.append("product", productId);
        submitedFormData.append("description", description);
        submitedFormData.append("showDescription", showDescription ? "1" : "0");

        selectedImages.forEach((file, index) => {
            submitedFormData.append(`image${index}`, file);
        });
        try {
            const response = await controller.onAdsCreateSubmit(submitedFormData);
            if (response && response.success === true) {
                navigate("/adm/ads-list", {
                    state: {
                        showToast: true,
                        text: strings.actions.created,
                        image: response.coverImage,
                        date: new Date().toISOString(),
                    },
                });
            } else if (!response.success && response!.error) {
                setToasting(
                    Array.isArray(response.response) ? response.response[0] : response.response
                );
            }
            setProccessing(false);
        } catch (error) {
            if (error === "401") {
                setCallbackLogin(
                    {
                        goBack: true,
                        origin: '/adm/dashboard',
                    }
                );
                handleLogout();
                navigate('/auth');
            }
        }
    };

    const content = (
        <>
            <h4 className="mb-4">{strings.title}</h4>
            <Card>
                <Card.Body>
                    <p className="text-muted mb-3">{strings.subtitle}</p>
                    <Form onSubmit={handleFormSubmit}>
                        <fieldset disabled={isProccessing}>
                            <FloatingLabel
                                className="mb-3"
                                controlId="adsDescription"
                                label={strings.description.label}
                            >
                                <Form.Control
                                    type="text"
                                    placeholder={strings.description.placeholder}
                                    className="text-capitalize"
                                    required
                                    autoComplete="off"
                                    aria-describedby="descriptionHelpBlock"
                                    value={description}
                                    onChange={(e) => {
                                        setFormData((prevData) => ({
                                            ...prevData,
                                            description: e.target.value,
                                        }));
                                    }}
                                />
                            </FloatingLabel>
                            <Form.Text className="d-none" id="descriptionHelpBlock">
                                {strings.description.help_block}
                            </Form.Text>

                            <Form.Check
                                type="switch"
                                id="custom-switch"
                                label={strings.showDescription.label}
                                onChange={(e) => {
                                    setFormData((prevData) => ({
                                        ...prevData,
                                        showDescription: e.target.checked,
                                    }));
                                }}
                                checked={showDescription}
                            />

                            <Form.Select
                                className="mt-4"
                                aria-label="product"
                                name="product"
                                aria-describedby="productHelpBlock"
                                onChange={(e) => {
                                    setFormData((prevData) => ({
                                        ...prevData,
                                        productId: e.target.value,
                                    }));
                                }}
                            >
                                <option value={""}>{strings.product.placeholder}</option>
                                {myProducts.map((product, index) => (
                                    <option key={index} value={product.uuid}>
                                        {product.part_name}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Text className="d-none" id="productHelpBlock">
                                Informe o produto que está vinculado a este banner. Ao concluir
                                pressione TAB para continuar.
                            </Form.Text>

                            <p className="text-muted mb-3 mt-4">{strings.images.title}</p>

                            <Form.Group controlId="formFileMultiple" className="mb-3">
                                <Form.Label>{strings.images.label}</Form.Label>
                                <Form.Control
                                    type="file"
                                    accept=".jpg, .jpeg, .png, .jfif, .webp"
                                    multiple
                                    required
                                    onChange={handleFileChange}
                                />
                            </Form.Group>
                            <Row className="mb-5">
                                <Col>
                                    {imagePreviews.map((preview, index) => (
                                        <Image
                                            key={index}
                                            src={preview}
                                            alt={`Preview ${index}`}
                                            style={{ maxWidth: 100, maxHeight: 100 }}
                                            thumbnail
                                        />
                                    ))}
                                </Col>
                            </Row>
                            <Button variant="primary" type="submit">
                                Gravar
                            </Button>
                        </fieldset>
                    </Form>
                </Card.Body>
            </Card>
            <ToastContainer position="top-end" className="p-3" style={{ zIndex: 1 }}>
                <Toast
                    bg={"danger"}
                    onClose={() => setToasting(null)}
                    show={toasting !== null ? true : false}
                    delay={5000}
                    autohide
                >
                    <Toast.Header>
                        <strong className="me-auto">Montacar</strong>
                        <small>
                            {toasting !== null
                                ? calculateTimeDifference(new Date().toISOString())
                                : "-"}
                        </small>
                    </Toast.Header>
                    <Toast.Body className={"text-white"}>
                        {toasting !== null ? toasting : null}
                    </Toast.Body>
                </Toast>
            </ToastContainer>
        </>
    );
    return <AdmPanelContainer children={content} />;
}
